import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  Calculator,
  Eye,
  MoreVertical,
  Mail,
  Trash2,
  Edit,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const ROLE_INFO = {
  owner: {
    label: 'Propriétaire',
    icon: Crown,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    description: 'Accès complet, peut gérer l\'entreprise'
  },
  admin: {
    label: 'Administrateur',
    icon: Shield,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    description: 'Tout sauf supprimer l\'entreprise'
  },
  accountant: {
    label: 'Comptable',
    icon: Calculator,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    description: 'Factures, devis, clients, dépenses'
  },
  reader: {
    label: 'Lecteur',
    icon: Eye,
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    description: 'Consultation uniquement'
  }
};

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('accountant');
  const [inviting, setInviting] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [deleteMember, setDeleteMember] = useState(null);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const canManageTeam = user?.team_role === 'owner' || user?.team_role === 'admin';

  const loadTeamMembers = useCallback(async () => {
    try {
      const response = await api.get('/team/members');
      setMembers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'équipe');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingInvitations = useCallback(async () => {
    try {
      const response = await api.get('/team/invitations/pending');
      setPendingInvitations(response.data);
    } catch (error) {
      console.error('Error loading pending invitations:', error);
    }
  }, []);

  useEffect(() => {
    loadTeamMembers();
    loadPendingInvitations();
  }, [loadTeamMembers, loadPendingInvitations]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    try {
      await api.post('/team/invite', {
        email: inviteEmail,
        team_role: inviteRole
      });
      toast.success(`Invitation envoyée à ${inviteEmail}`);
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('accountant');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      const response = await api.post(`/team/invitations/${invitationId}/accept`);
      toast.success(response.data.message);
      loadPendingInvitations();
      loadTeamMembers();
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'acceptation');
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await api.post(`/team/invitations/${invitationId}/decline`);
      toast.success('Invitation refusée');
      loadPendingInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateRole = async () => {
    if (!editMember || !newRole) return;

    try {
      await api.put(`/team/members/${editMember.id}/role`, {
        team_role: newRole
      });
      toast.success('Rôle mis à jour');
      setEditMember(null);
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleRemoveMember = async () => {
    if (!deleteMember) return;

    try {
      await api.delete(`/team/members/${deleteMember.id}`);
      toast.success('Membre retiré de l\'équipe');
      setDeleteMember(null);
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/team/invitations/${invitationId}`);
      toast.success('Invitation annulée');
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Équipe</h1>
          <p className="text-slate-500 mt-1">Gérez les membres de votre entreprise</p>
        </div>
        {canManageTeam && (
          <Button 
            onClick={() => setInviteOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="invite-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Inviter un membre
          </Button>
        )}
      </div>

      {/* Pending invitations for current user */}
      {pendingInvitations.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Invitations en attente</h2>
          </div>
          <div className="space-y-3">
            {pendingInvitations.map((inv) => (
              <div 
                key={inv.id} 
                className="flex items-center justify-between bg-white p-4 rounded-lg border border-amber-200"
              >
                <div>
                  <p className="font-medium text-slate-900">{inv.company_name}</p>
                  <p className="text-sm text-slate-500">
                    Rôle: {ROLE_INFO[inv.team_role]?.label || inv.team_role}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeclineInvitation(inv.id)}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Refuser
                  </Button>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleAcceptInvitation(inv.id)}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Accepter
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles explanation */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(ROLE_INFO).map(([role, info]) => {
          const Icon = info.icon;
          return (
            <div 
              key={role} 
              className={`${info.bgColor} rounded-xl p-4 border`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${info.color}`} />
                <span className="font-semibold text-slate-900">{info.label}</span>
              </div>
              <p className="text-xs text-slate-600">{info.description}</p>
            </div>
          );
        })}
      </div>

      {/* Team members list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Membres ({members.filter(m => m.status === 'active').length})
          </h2>
        </div>

        {members.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun membre dans l'équipe</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {members.map((member) => {
              const roleInfo = ROLE_INFO[member.team_role] || ROLE_INFO.reader;
              const Icon = roleInfo.icon;
              const isCurrentUser = member.email === user?.email;
              const isPending = member.status === 'pending';

              return (
                <div 
                  key={member.id} 
                  className={`p-4 flex items-center justify-between hover:bg-slate-50 transition-colors ${isPending ? 'bg-slate-50/50' : ''}`}
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full ${roleInfo.bgColor} flex items-center justify-center`}>
                      {isPending ? (
                        <Clock className="w-5 h-5 text-slate-400" />
                      ) : (
                        <Icon className={`w-5 h-5 ${roleInfo.color}`} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">
                          {member.name || member.email}
                        </span>
                        {isCurrentUser && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Vous
                          </span>
                        )}
                        {isPending && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            En attente
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Mail className="w-3 h-3" />
                        {member.email}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${roleInfo.bgColor} ${roleInfo.color}`}>
                      {roleInfo.label}
                    </div>

                    {canManageTeam && !isCurrentUser && member.team_role !== 'owner' && (
                      <div className="flex items-center gap-1">
                        {isPending ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvitation(member.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditMember(member);
                                setNewRole(member.team_role);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteMember(member)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviter un membre</DialogTitle>
            <DialogDescription>
              Envoyez une invitation par email pour rejoindre votre équipe.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="collaborateur@exemple.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="role">Rôle</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      Administrateur
                    </div>
                  </SelectItem>
                  <SelectItem value="accountant">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-emerald-500" />
                      Comptable
                    </div>
                  </SelectItem>
                  <SelectItem value="reader">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-500" />
                      Lecteur
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={inviting || !inviteEmail}
                data-testid="send-invite-btn"
              >
                {inviting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer l'invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
            <DialogDescription>
              Changer le rôle de {editMember?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrateur</SelectItem>
                <SelectItem value="accountant">Comptable</SelectItem>
                <SelectItem value="reader">Lecteur</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMember(null)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateRole}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteMember} onOpenChange={() => setDeleteMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteMember?.email} sera retiré de l'équipe et perdra l'accès aux données de l'entreprise.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-red-600 hover:bg-red-700"
            >
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
